// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import { InstrumentIdentifier } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Types';
import DynamicListInput from '../../components/Form/DynamicListInput';
import TextInput from '../../components/Form/TextInput';
import { InstrumentIdentifierElement } from './Common';

interface InstrumentIdentifiersInputProps {
  instrumentIdentifiers: InstrumentIdentifierElement[];
  setInstrumentIdentifiers: React.Dispatch<React.SetStateAction<InstrumentIdentifierElement[]>>;
  title: string;
}

const InstrumentIdentifiersInput: React.FC<InstrumentIdentifiersInputProps> = ({
  instrumentIdentifiers,
  setInstrumentIdentifiers,
  title,
}) => {
  // Helper to update a specific instrument within the list.
  const updateInstrument = (
    instrumentId: string,
    field: keyof InstrumentIdentifier,
    value: string,
  ) => {
    setInstrumentIdentifiers((prev: InstrumentIdentifierElement[]) =>
      prev.map((instrument) =>
        instrument.id === instrumentId
          ? { ...instrument, element: { ...instrument.element, [field]: value } }
          : instrument,
      ),
    );
  };

  return (
    <DynamicListInput<InstrumentIdentifierElement>
      list={instrumentIdentifiers}
      setList={setInstrumentIdentifiers}
      title={title}
      createNewItem={() => ({
        id: uuidv4(),
        element: {
          source: '',
          id: '',
          scheme: '',
        },
      })}
      renderItem={(instrumentIdentifier: InstrumentIdentifierElement) => (
        <Box display="flex" gap={1} mb={0}>
          <TextInput
            label="Source"
            value={instrumentIdentifier.element.source}
            setValue={(value) =>
              updateInstrument(instrumentIdentifier.id, 'source', value as string)
            }
            required
          />
          <TextInput
            label="Id"
            value={instrumentIdentifier.element.id}
            setValue={(value) => updateInstrument(instrumentIdentifier.id, 'id', value as string)}
            required
          />
          <TextInput
            label="Scheme"
            value={instrumentIdentifier.element.scheme}
            setValue={(value) =>
              updateInstrument(instrumentIdentifier.id, 'scheme', value as string)
            }
          />
        </Box>
      )}
    />
  );
};

export default InstrumentIdentifiersInput;
